import {
  IDropZoneStepGroup,
  IDropZoneWorkflowStep,
} from 'interfaces/designOrchestration/designOrchestrationDiagram.interface';
import { IModuleOnDrag } from 'interfaces/designOrchestration/designOrchestrationModules.interface';
import { IDropZoneWorkflowState } from 'interfaces/designOrchestration/designOrchestrationState.interface';
import { IOrchestrationFullStep } from 'interfaces/orchestration.interface';
import { IOrchestrationNode } from 'interfaces/orchestrationDiagram/orchestrationDiagram.interface';
import {
  getSteps,
  processOrchestration,
} from 'utils/reactFlow/reactFlowHelpers';

import {
  DROPZONES_NAMES,
  DROPZONE_TYPES,
  DEFAULT_NODES_IDS,
  WORKFLOW_INITIAL_OBJECT,
} from '../../DesignOrchestrationDiagram.constans';
import {
  addNestedFailback,
  addNestedModule,
  addParallelModule,
  addPredecessorModule,
  addSucceedingModule,
} from '../dropZoneHandlers/dropZoneHandlers';
import { includePropertiesInCleanStep } from 'utils/commonFunctions/CommonFunctions';

const MODULE_PARALLEL_OFFSET = 130;

const handleDropZoneType = (
  parent: any,
  key: string,
  found: any,
  module: IModuleOnDrag,
  targetWorkflowStepId: string,
  currentState?: IDropZoneWorkflowState,
  isGlobalFailback = false
): void => {
  switch (found.stepId) {
    case DROPZONE_TYPES.SUCCEDING:
    case DROPZONE_TYPES.GLOBAL_FAILBACK_STEPS:
      parent[key] = addSucceedingModule(
        module,
        targetWorkflowStepId,
        currentState,
        isGlobalFailback
      );
      break;
    case DROPZONE_TYPES.SUCCEDING_FAILBACK:
      parent[key] = addNestedFailback(
        module,
        targetWorkflowStepId,
        true,
        currentState
      );
      break;
    case DROPZONE_TYPES.PARALLEL:
      const newParallelModule = addParallelModule(
        module,
        targetWorkflowStepId,
        parent[key],
        false,
        false
      );

      if (parent?.steps?.length > 1) {
        parent.steps = newParallelModule.steps;
        parent[key] = newParallelModule.nextStepGroup;
      } else {
        parent[key] = newParallelModule;
        parent.steps = parent?.steps?.map((step: any) => ({
          ...step,
        }));
      }

      break;
    case DROPZONE_TYPES.PARALLEL_FAILBACK:
      const newParallelFailbackModule = addParallelModule(
        module,
        targetWorkflowStepId,
        parent[key],
        true,
        false
      );

      if (parent?.steps?.length > 1) {
        parent.steps = newParallelFailbackModule.steps;
        parent[key] = newParallelFailbackModule.nextStepGroup;
      } else {
        parent[key] = newParallelFailbackModule;
        parent.steps = parent?.steps?.map((step: any) => ({
          ...step,
        }));
      }
      break;
    case DROPZONE_TYPES.NESTED:
      parent[key] = addNestedModule(module, targetWorkflowStepId);
      break;
    case DROPZONE_TYPES.NESTED_FAILBACK:
      parent[key] = addNestedFailback(
        module,
        targetWorkflowStepId,
        false,
        currentState
      );

      break;
    case DROPZONE_TYPES.NESTED_GLOBAL_FAILBACK:
      parent[key] = addNestedFailback(
        module,
        targetWorkflowStepId,
        false,
        currentState,
        true
      );
      break;
    case DROPZONE_TYPES.PREDECESSOR:
      // Preserve siblings: pass the current dropzone group and full state
      const currentGroup = parent[key] as IDropZoneStepGroup;
      parent[key] = addPredecessorModule(
        module,
        targetWorkflowStepId,
        currentGroup,
        false,
        isGlobalFailback,
        currentState
      );
      break;
    case DROPZONE_TYPES.PREDECESSOR_FAILBACK:
      // Preserve siblings: pass the current dropzone group and full state
      const currentGroupFailback = parent[key] as IDropZoneStepGroup;
      parent[key] = addPredecessorModule(
        module,
        targetWorkflowStepId,
        currentGroupFailback,
        true,
        isGlobalFailback,
        currentState
      );

      break;
    case DROPZONE_TYPES.PARALLEL_GLOBAL_FAILBACK:
      const newParallelGlobalFailbackModule = addParallelModule(
        module,
        targetWorkflowStepId,
        parent[key],
        false,
        true
      );
      if (parent?.steps?.length > 1) {
        parent.steps = newParallelGlobalFailbackModule.steps;
        parent[key] = newParallelGlobalFailbackModule.nextStepGroup;
      } else {
        parent[key] = newParallelGlobalFailbackModule;
        parent.steps = parent?.steps?.map((step: any) => ({
          ...step,
        }));
      }
      break;
    default:
      break;
  }
};

export function addModuleInDropzone(
  workflow: IDropZoneWorkflowState,
  targetWorkflowStepId: string,
  module: IModuleOnDrag,
  currentState?: IDropZoneWorkflowState,
  isGlobalFailback = false
): IDropZoneWorkflowState | boolean {
  const traverseGroup = (
    parent: any,
    key: string
  ): IDropZoneWorkflowState | boolean => {
    const group = parent[key];
    if (!group) {
      return false;
    }
    if (Array.isArray(group.steps)) {
      const found = group.steps.find(
        (step: any) => step.workflowStepId?.toString() === targetWorkflowStepId
      );
      if (found) {
        handleDropZoneType(
          parent,
          key,
          found,
          module,
          targetWorkflowStepId,
          currentState,
          isGlobalFailback
        );
        return workflow;
      }
    }
    // Recursively traverse nextStepGroup if exists
    if (group.nextStepGroup) {
      const result = traverseGroup(group, 'nextStepGroup');
      if (result) {
        return result;
      }
    }

    // Replace forEach with for loop to allow proper returns
    for (const step of group.steps) {
      const result = checkStepGroup(step, [
        'failbackStepGroup',
        'nestedStepGroup',
      ]);
      if (result) {
        return result;
      }
    }

    return false;
  };
  const checkStepGroup = (step: any, groupTypes: string[]): any | undefined => {
    for (const groupType of groupTypes) {
      if (step[groupType]) {
        const result = traverseGroup(step, groupType);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  };
  if (isGlobalFailback) {
    return traverseGroup(workflow.workflow, 'failbackStepGroup');
  }
  return traverseGroup(workflow.workflow, 'nextStepGroup');
}

export function updateWorkflowState(
  targetWorkflowStepId: string,
  currentState: IDropZoneWorkflowState,
  module: IModuleOnDrag,
  isGlobalFailback = false
): IDropZoneWorkflowState {
  try {
    const workflow = JSON.parse(
      JSON.stringify(currentState)
    ) as IDropZoneWorkflowState;
    const updated = addModuleInDropzone(
      workflow,
      targetWorkflowStepId,
      module,
      currentState,
      isGlobalFailback
    );

    if (!updated) {
      console.error('updateWorkflowState: Target workflow step not found.');
      return currentState;
    }
    return updated as IDropZoneWorkflowState;
  } catch (error) {
    console.error('updateWorkflowState: Error updating workflow state:', error);
    return currentState;
  }
}

const generateBackgroundNodes = (
  nodes: IOrchestrationNode[],
  flattenedSteps: IOrchestrationFullStep[]
) => {
  const NESTED_BACKGROUND_PADDING_X = 8;
  const NESTED_BACKGROUND_PADDING_Y = 16.5;

  const groupedNodes = nodes.reduce(
    (groups: Record<string, any[]>, node: IOrchestrationNode) => {
      const matchingStep = flattenedSteps.find(
        (step: any) => String(step.workflowStepId) === String(node.id)
      );
      if (node.data?.workflowStepGroupId && node.data?.isNestedStep) {
        const groupId = node.data.workflowStepGroupId;
        if (!groups[groupId]) {
          groups[groupId] = [];
        }
        if (
          !groups[groupId].some((existingNode) => existingNode.id === node.id)
        ) {
          groups[groupId].push(node);
        }
      }

      if (
        node.data?.isNestedStep &&
        matchingStep &&
        Array.isArray(matchingStep.nestGroupId)
      ) {
        matchingStep.nestGroupId.forEach((ancestorId: string) => {
          if (ancestorId) {
            if (!groups[ancestorId]) {
              groups[ancestorId] = [];
            }
            if (
              !groups[ancestorId].some(
                (existingNode) => existingNode.id === node.id
              )
            ) {
              groups[ancestorId].push(node);
            }
          }
        });
      }

      return groups;
    },
    {}
  );

  return Object.entries(groupedNodes)
    .map(([groupId, groupNodes]) => {
      const groupNode = flattenedSteps.find(
        (step: any) => String(step.workflowStepId) === String(groupId)
      );
      const foundStep = flattenedSteps.find(
        (step: any) => String(step.workflowStepId) === String(groupId)
      );
      if (!foundStep?.workflowStepGroupId) {
        return null;
      }

      if (
        !groupNode?.workflowStepGroupId &&
        groupNode?.workflowStepGroupId !== null
      ) {
        return null;
      }

      const groupNodePositions = groupNodes.map((node: IOrchestrationNode) => ({
        x: node.position.x,
        y: node.position.y,
        width: 241, // Default width
        height: 50, // Default height
      }));

      // Ensure we have nodes in this group before calculating dimensions
      if (groupNodePositions.length === 0) {
        return null;
      }

      try {
        const minX =
          Math.min(...groupNodePositions.map((pos) => pos.x)) -
          NESTED_BACKGROUND_PADDING_X;
        const minY =
          Math.min(...groupNodePositions.map((pos) => pos.y)) -
          NESTED_BACKGROUND_PADDING_Y;
        const maxX =
          Math.max(...groupNodePositions.map((pos) => pos.x + pos.width)) +
          NESTED_BACKGROUND_PADDING_X;
        const maxY =
          Math.max(...groupNodePositions.map((pos) => pos.y + pos.height)) +
          NESTED_BACKGROUND_PADDING_Y;

        const width = maxX - minX;
        const height = maxY - minY;

        const stepType = groupNodes.map(
          (node: IOrchestrationNode) => node.data.stepType
        )[0];

        return {
          id: `bg-${groupId}`,
          type: 'backgroundGroupNode',
          position: { x: minX, y: minY },
          data: {
            width,
            height,
            stepType,
          },
          zIndex: -1,
        };
      } catch (error) {
        console.error(
          'Error calculating background dimensions for group:',
          groupId,
          error
        );
        return null;
      }
    })
    .filter((node) => node !== null);
};

export function generateFlowNodesFromWorkflow(
  orchestration: IDropZoneWorkflowState,
  containerWidth: number
): { nodes: any[]; edges: any[] } {
  try {
    if (!orchestration?.workflow?.nextStepGroup) {
      return { nodes: [], edges: [] };
    }
    let workflow = JSON.parse(JSON.stringify(orchestration));
    workflow = assignStepIndexes(workflow);

    let steps: IOrchestrationFullStep[][] = [];
    steps = getSteps(workflow.workflow.nextStepGroup, steps, false, 'step');
    let globalFailbackSteps: IOrchestrationFullStep[][] = [];
    globalFailbackSteps = getSteps(
      workflow.workflow.failbackStepGroup,
      globalFailbackSteps,
      false,
      'globalFailback'
    );

    const { nodes } = processOrchestration(
      workflow,
      containerWidth - containerWidth * 0.25,
      [...steps, ...globalFailbackSteps],
      true
    );
    const flattenedSteps = [...steps, ...globalFailbackSteps]
      .flat()
      .filter((step: any) => step !== undefined);
    let workflowStepIdsWithChildren: number[] = [];

    [...steps, ...globalFailbackSteps].forEach(
      (stepGroup: IOrchestrationFullStep[]) => {
        // check if some parallel step has children
        const hasNestedChildren = stepGroup?.some(
          (step) =>
            step?.failbackStepGroup?.steps?.some(
              ({ stepId }: { stepId: DROPZONE_TYPES }) =>
                stepId !== DROPZONE_TYPES.NESTED_FAILBACK
            ) ||
            step?.nestedStepGroup?.steps?.some(
              ({ stepId }: { stepId: DROPZONE_TYPES }) =>
                stepId !== DROPZONE_TYPES.NESTED &&
                stepId !== DROPZONE_TYPES.NESTED_GLOBAL_FAILBACK
            )
        );
        if (hasNestedChildren) {
          workflowStepIdsWithChildren = [
            ...workflowStepIdsWithChildren,
            ...stepGroup.map(({ workflowStepId }) => workflowStepId),
          ];
        }
      }
    );
    nodes.forEach((node: IOrchestrationNode) => {
      const matchingStep = flattenedSteps.find(
        (step: any) => String(step.workflowStepId) === String(node.id)
      );
      if (matchingStep) {
        node.data.isNestedStep = matchingStep.isNestedStep;
        node.data.index = matchingStep.stepIndex;
        node.data.isOnDesignOrchestration = true;
        node.data.hasNestedChildren = workflowStepIdsWithChildren.includes(
          matchingStep.workflowStepId
        );

        node.data.workflowStepGroupId =
          matchingStep.nestGroupId !== undefined &&
          flattenedSteps.some(
            (n) => n.workflowStepId === Number(matchingStep.workflowStepGroupId)
          ) &&
          flattenedSteps.find(
            (n) => n.workflowStepId === Number(matchingStep.workflowStepGroupId)
          )?.isNestedStep
            ? matchingStep.workflowStepGroupId
            : undefined;
      }
    });

    AlignRootSucceedingAndPredecessor(nodes, [
      ...steps,
      ...globalFailbackSteps,
    ]);

    const backgroundNodes = generateBackgroundNodes(nodes, flattenedSteps);
    const allNodes = [...backgroundNodes, ...nodes];
    return { nodes: allNodes, edges: [] };
  } catch (error) {
    console.error(
      'generateFlowNodesFromWorkflow: Error generating flow nodes:',
      error
    );
    return { nodes: [], edges: [] };
  }
}

export function formatXMLWorkflow(obj: any): any {
  if (Array.isArray(obj)) {
    return obj
      .map((item) => formatXMLWorkflow(item))
      .flatMap((item) => {
        // If the stepTitle matches DROPZONES_NAMES, unwrap its nestedStepGroup or nextStepGroup
        if (
          item &&
          typeof item === 'object' &&
          Object.values(DROPZONES_NAMES).includes(item.stepTitle)
        ) {
          if (item.nestedStepGroup) {
            return formatXMLWorkflow(item.nestedStepGroup.steps); // Replace the step with its nestedStepGroup steps
          } else if (item.nextStepGroup) {
            return formatXMLWorkflow(item.nextStepGroup.steps); // Replace the step with its nextStepGroup steps
          }
          return []; // Remove the item if it has no valid replacements
        }
        // Remove UI-specific properties but preserve step configuration properties
        if (item.nestGroupId || item.associatedMainNodeId || item.stepType) {
          const {
            nestGroupId,
            associatedMainNodeId,
            stepType,
            uniqueId,
            // Preserve these step-level properties when exporting to XML
            stepId,
            stepCondition,
            retryNumber,
            retryDelay,
            retryDelayMultiplier,
            ...otherProps
          } = item;

          // Create the cleaned object with preserved properties
          const cleanedStep = { ...otherProps };

          return includePropertiesInCleanStep(cleanedStep, {
            stepId,
            stepCondition,
            retryNumber,
            retryDelay,
            retryDelayMultiplier,
          });
        }

        return item; // Keep unchanged item
      });
  }

  if (obj !== null && typeof obj === 'object') {
    const newObj: Record<string, any> = {};
    for (const key of Object.keys(obj)) {
      if (key === 'steps' && Array.isArray(obj[key])) {
        // Process `steps` array and unwrap replacements
        newObj[key] = obj[key]
          .map((step: any) => formatXMLWorkflow(step))
          .flatMap((step: any) => {
            if (
              step &&
              typeof step === 'object' &&
              Object.values(DROPZONES_NAMES).includes(step.stepTitle)
            ) {
              if (step.nestedStepGroup) {
                return formatXMLWorkflow(step.nestedStepGroup.steps); // Replace step with its nestedStepGroup steps
              } else if (step.nextStepGroup) {
                return formatXMLWorkflow(step.nextStepGroup.steps); // Replace step with its nextStepGroup steps
              }
              return []; // Remove the step if it has no valid replacements
            }

            // TODO: improve to dynamic key removing to avoid custom new fields issues
            // Remove UI-specific properties but preserve step configuration properties
            if (
              step.nestGroupId ||
              step.associatedMainNodeId ||
              step.stepType
            ) {
              const {
                nestGroupId,
                associatedMainNodeId,
                stepType,
                uniqueId,
                // Preserve these step-level properties when exporting to XML
                stepId,
                stepCondition,
                retryNumber,
                retryDelay,
                retryDelayMultiplier,
                ...otherProps
              } = step;

              // Create the cleaned object with preserved properties
              const cleanedStep = { ...otherProps };

              // Only include properties that are defined
              return includePropertiesInCleanStep(cleanedStep, {
                stepId,
                stepCondition,
                retryNumber,
                retryDelay,
                retryDelayMultiplier,
              });
            }

            return step; // Keep unchanged step
          });
      } else {
        newObj[key] = formatXMLWorkflow(obj[key]); // Recursively process other keys
      }
    }
    return newObj;
  }

  return obj;
}

export function flattenWorkflow(workflow: any): any {
  // Start flattening the main `nextStepGroup`
  workflow.nextStepGroup = flattenStepGroup(workflow.nextStepGroup);
  // workflow.failbackStepGroup = flattenStepGroup(workflow.failbackStepGroup);
  // Return the cleaned structure
  return workflow;
}

function flattenStepGroup(stepGroup: any | null): any | null {
  if (!stepGroup) {
    return null; // If there's no group, return null
  }

  // Continuously replace empty `nextStepGroup` until we hit a valid group
  while (stepGroup?.steps.length === 0 && stepGroup.nextStepGroup) {
    stepGroup = stepGroup.nextStepGroup;
  }

  // Recursively clean sub-properties within its updated `nextStepGroup`
  stepGroup.nextStepGroup = flattenStepGroup(stepGroup.nextStepGroup);

  // Recursively clean `steps` and their nested groups
  stepGroup.steps = stepGroup.steps.map(cleanStep);

  return stepGroup.steps?.length > 0 ? stepGroup : null;
}

function cleanStep(step: any): any {
  // Clean `nestedStepGroup` recursively
  step.nestedStepGroup = flattenStepGroup(step.nestedStepGroup);
  if (step.nestedStepGroup?.steps.length === 0) {
    step.nestedStepGroup = null; // Remove empty nested groups
  }

  // Clean `failbackStepGroup` recursively
  step.failbackStepGroup = flattenStepGroup(step.failbackStepGroup);
  if (step.failbackStepGroup?.steps.length === 0) {
    step.failbackStepGroup = null; // Remove empty failback groups
  }

  return step;
}

function AlignRootSucceedingAndPredecessor(
  nodes: IOrchestrationNode[],
  steps: IOrchestrationFullStep[][]
) {
  const flattenedSteps = steps.flat();
  const nodeMap = new Map(
    nodes.map((node: IOrchestrationNode) => [node.id, node])
  );

  // Helper Function: Check if a node belongs to a parallel group
  const isNodeInParallelGroup = (
    mainNodeId: string,
    flattenedSteps: IOrchestrationFullStep[],
    steps: IOrchestrationFullStep[][]
  ) => {
    const mainNodeStep = flattenedSteps.find(
      (step) => String(step.workflowStepId) === mainNodeId
    );
    if (!mainNodeStep) {
      return false;
    }

    const groupContainingMainNode = steps.find((group) =>
      group.some((step) => step.workflowStepId === mainNodeStep.workflowStepId)
    );

    return groupContainingMainNode && groupContainingMainNode.length > 2;
  };

  // Align nodes
  nodes.forEach((node: IOrchestrationNode) => {
    const currentNodeStep = flattenedSteps.find(
      (step) => String(step.workflowStepId) === node.id
    );

    const hastGroupdId = !!node.data?.workflowStepGroupId;
    const groupedNodes = nodes.filter((rawNode) => {
      const rawMatchingStep = flattenedSteps.find(
        (step) => String(step.workflowStepId) === rawNode.id
      );
      const filteredNestGroupids = hastGroupdId
        ? currentNodeStep?.nestGroupId?.filter(
            (groupId) => groupId === String(node.data.associatedMainNodeId)
          )
        : currentNodeStep?.nestGroupId;

      return (
        filteredNestGroupids?.some((item) =>
          rawMatchingStep?.nestGroupId?.includes(item)
        ) ||
        filteredNestGroupids?.includes(
          String(rawNode.data.associatedMainNodeId)
        )
      );
    });

    const minX = Math.min(
      ...groupedNodes.map((groupedNode) => groupedNode.position?.x)
    );
    const maxX = Math.max(
      ...groupedNodes.map((groupedNode) => groupedNode.position?.x)
    );
    const associatedMainNodeId = node.data?.associatedMainNodeId;
    const mainNode = associatedMainNodeId
      ? nodeMap.get(String(associatedMainNodeId))
      : null;

    if (mainNode && isPredecessorOrSucceedingNode(node)) {
      // Check if the current node belongs to a parallel structure
      const isParallel = isNodeInParallelGroup(
        String(mainNode.id),
        flattenedSteps,
        steps
      );

      if (isParallel && isAloneInGroup(node, steps)) {
        const position = (minX + maxX) / 2;
        node.position.x = position - MODULE_PARALLEL_OFFSET;
      } else {
        // Default alignment with associated main node
        node.position.x = mainNode.position.x;
      }
    }
  });
}

// Helper Function: Determine if a node is Predecessor or Succeeding
function isPredecessorOrSucceedingNode(node: IOrchestrationNode): boolean {
  return (
    node.data.name === DROPZONES_NAMES.PREDECESSOR ||
    node.data.name === DROPZONES_NAMES.SUCCEDING_FAILBACK ||
    node.data.name === DROPZONES_NAMES.SUCCEDING ||
    node.data.name === DROPZONES_NAMES.PREDECESSOR_FAILBACK
  );
}

// Helper Function: Check if a node is alone in its group
function isAloneInGroup(
  node: IOrchestrationNode,
  steps: IOrchestrationFullStep[][]
): boolean {
  const nodeGroup = steps.find((group) =>
    group.some((step) => String(step.workflowStepId) === String(node.id))
  );
  return nodeGroup?.length === 1;
}

// Assigns stepIndex to each step in the workflow based on type
export function assignStepIndexes(
  workflowState: IDropZoneWorkflowState
): IDropZoneWorkflowState {
  let normalCounter = 0;
  let failbackCounter = 0;
  function traverseStepGroup(group: IDropZoneStepGroup | null): void {
    if (!group) {
      return;
    }
    for (const step of group.steps) {
      const isDropZone = Object.values(DROPZONES_NAMES).includes(
        step.stepTitle as any
      );
      if (!isDropZone) {
        if (
          step.stepTitle?.toString().toLowerCase().includes('failback') ||
          step.stepType?.toString().toLowerCase().includes('failback')
        ) {
          failbackCounter++;
          step.stepIndex = `E${failbackCounter}`;
        } else {
          normalCounter++;
          step.stepIndex = normalCounter;
        }
      }
      traverseStepGroup(step.nestedStepGroup);
      traverseStepGroup(step.failbackStepGroup);
    }
    traverseStepGroup(group.nextStepGroup);
  }

  traverseStepGroup(workflowState.workflow.nextStepGroup);
  traverseStepGroup(workflowState.workflow.failbackStepGroup);

  return workflowState;
}

export function extractStepIndexByUniqueId(
  workflowState: IDropZoneWorkflowState
): Record<string, string | number> {
  const stepIndexMap: Record<string, string | number> = {};

  function traverseStepGroup(group: IDropZoneStepGroup | null): void {
    if (!group) {
      return;
    }
    for (const step of group.steps) {
      if (
        step.uniqueId &&
        step.stepIndex !== null &&
        step.stepIndex !== undefined
      ) {
        const isDropZone = Object.values(DROPZONES_NAMES).includes(
          step.stepTitle as any
        );

        if (!isDropZone) {
          stepIndexMap[step.uniqueId] = step.stepIndex;
        }
      }
      traverseStepGroup(step.nestedStepGroup);
      traverseStepGroup(step.failbackStepGroup);
    }
    traverseStepGroup(group.nextStepGroup);
  }

  traverseStepGroup(workflowState.workflow.nextStepGroup);
  traverseStepGroup(workflowState.workflow.failbackStepGroup);

  return stepIndexMap;
}
